import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Brand loader.
 * Phase 0 (0-1.5s) — three short lines that explain what thai gayu does.
 * Phase 1 (1.5-4.0s) — gold logo + brand tagline.
 * Phase 2 (4.0+) — exit wipe.
 */
const POEMS = [
  "the right hour.",
  "for the right move.",
  "thai gayu means it is done.",
];

const LOADER_SEEN_KEY = "thaigayu_loader_seen";

// Mobile devices and repeat visitors get a much shorter (or no) loader.
// First-time desktop visitors keep the full brand experience.
function getLoaderMode() {
  if (typeof window === "undefined") return "full";
  try { if (sessionStorage.getItem(LOADER_SEEN_KEY)) return "skip"; } catch { /* ignore */ }
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return "skip";
  if (window.matchMedia("(max-width: 768px)").matches) return "short";
  return "full";
}

export default function Loader({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [phase, setPhase] = useState(0);
  const mode = React.useMemo(getLoaderMode, []);

  // Bail out immediately for repeat visitors / reduced motion.
  useEffect(() => {
    if (mode === "skip") {
      setDone(true);
      onDone?.();
    }
  }, [mode, onDone]);

  // Mark the loader as seen as soon as the component mounts so the next
  // visit (any tab, any route) skips it.
  useEffect(() => {
    try { sessionStorage.setItem(LOADER_SEEN_KEY, "1"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (mode === "skip") return;
    // Short mode skips the poem phase entirely.
    if (mode === "short") {
      setPhase(1);
      return;
    }
    const t1 = setTimeout(() => setPhase(1), 1500);
    return () => clearTimeout(t1);
  }, [mode]);

  useEffect(() => {
    if (phase !== 1 || mode === "skip") return;
    let raf;
    const start = performance.now();
    const DUR = mode === "short" ? 700 : 2400;
    const exitDelay = mode === "short" ? 120 : 400;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / DUR);
      const eased = 1 - Math.pow(1 - p, 3);
      setProgress(Math.floor(eased * 100));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setTimeout(() => { setDone(true); onDone?.(); }, exitDelay);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase, mode, onDone]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 1.0, ease: [0.7, 0, 0.2, 1] }}
          className="fixed inset-0 z-[9000] flex flex-col overflow-hidden"
          style={{ background: "var(--teal-deeper)" }}
        >
          {/* warm vignette */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(900px 600px at 50% 38%, rgba(82,124,120,0.42) 0%, transparent 60%)," +
                "radial-gradient(700px 480px at 50% 92%, rgba(184,155,94,0.16) 0%, transparent 60%)",
            }}
          />

          {/* TOP corners */}
          <div className="relative z-10 flex items-start justify-between p-6 md:p-10">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full pulse-ring" style={{ background: "var(--accent)" }} />
              <span className="corner-label" style={{ color: "var(--ink-muted)" }}>
                ancient science · modern timing
              </span>
            </div>
            <span className="corner-label" style={{ color: "var(--ink-muted)" }}>
              sixteen windows · one day
            </span>
          </div>

          {/* CENTER — fully centered logo / mantra */}
          <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
            <AnimatePresence mode="wait">
              {phase === 0 && (
                <motion.div
                  key="poem"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.7 }}
                  className="flex flex-col gap-2"
                >
                  {POEMS.map((line, i) => (
                    <motion.div
                      key={line}
                      initial={{ y: 24, opacity: 0 }}
                      animate={{ y: 0, opacity: i === 2 ? 0.95 : 0.92 }}
                      transition={{ duration: 0.7, delay: i * 0.18, ease: [0.7, 0, 0.2, 1] }}
                      className={
                        i === 2
                          ? "font-display italic text-[clamp(1.2rem,3vw,2rem)] leading-none mt-2"
                          : "font-display text-[clamp(2rem,5vw,3.5rem)] leading-none"
                      }
                      style={{ color: i === 2 ? "var(--accent-soft)" : "var(--ink-2)" }}
                    >
                      {line}
                    </motion.div>
                  ))}
                </motion.div>
              )}
              {phase === 1 && (
                <motion.div
                  key="brand"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7 }}
                  className="flex flex-col items-center"
                >
                  <motion.img
                    src="/thaigayu-logo-gold.png"
                    alt="Thai Gayu"
                    width="460"
                    height="142"
                    decoding="async"
                    initial={{ y: 30, opacity: 0, scale: 0.92 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, ease: [0.7, 0, 0.2, 1] }}
                    className="w-[min(70vw,460px)] h-auto select-none"
                    style={{
                      mixBlendMode: "lighten",
                      filter:
                        "drop-shadow(0 12px 32px rgba(0,0,0,0.55)) drop-shadow(0 0 26px rgba(184,155,94,0.35))",
                    }}
                    draggable={false}
                  />
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.88 }}
                    transition={{ duration: 1.0, delay: 0.5 }}
                    className="font-display italic text-base md:text-xl mt-6"
                    style={{ color: "var(--ink-muted)" }}
                  >
                    time is not clocks. <em style={{ color: "var(--accent-soft)" }}>it is rhythm.</em>
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* BOTTOM — brass dial sketch + tick marks + progress bar */}
          <div className="relative z-10 p-6 md:p-10">
            {/* sketch ring + ticks (decorative) */}
            <div className="mx-auto mb-6 relative" style={{ width: "min(86vw, 720px)", height: "82px" }}>
              <svg viewBox="0 0 720 80" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
                {/* horizontal arc */}
                <motion.path
                  d="M 20 60 Q 360 -30 700 60"
                  fill="none" stroke="#B89B5E"
                  strokeOpacity="0.55" strokeWidth="1"
                  strokeDasharray="900"
                  initial={{ strokeDashoffset: 900 }}
                  animate={{ strokeDashoffset: 0 }}
                  transition={{ duration: 2.6, ease: [0.7, 0, 0.2, 1] }}
                />
                {/* tick marks */}
                {Array.from({ length: 32 }).map((_, i) => {
                  const t = i / 31;
                  const x = 20 + (700 - 20) * t;
                  const long = i % 4 === 0;
                  // y on arc
                  const u = t * 2 - 1;
                  const y = 60 + (-30 - 60) * (1 - u * u);
                  return (
                    <motion.line
                      key={i}
                      x1={x} y1={y - 4}
                      x2={x} y2={y + (long ? 12 : 8)}
                      stroke="#D8B97A"
                      strokeOpacity={long ? 0.85 : 0.45}
                      strokeWidth={long ? 1.4 : 0.8}
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.4 + i * 0.03 }}
                    />
                  );
                })}
              </svg>
            </div>

            <div className="flex items-end justify-between gap-6 mb-3">
              <span className="corner-label">sunrise · sunset · ghadi · panchang</span>
              <span className="numeric text-sm" style={{ color: "var(--ink)" }}>
                {String(progress).padStart(3, "0")} / 100
              </span>
            </div>
            <div className="h-px w-full relative overflow-hidden" style={{ background: "rgba(216,185,122,0.20)" }}>
              <motion.div
                className="absolute inset-y-0 left-0"
                style={{ background: "var(--accent)" }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
